<template>
    <div>
        <p v-if="error" class="text-danger">{{errorMsg}}</p>
        
        <b-overlay :show="loading">
        
        <b-form-row class="">

            <div 
                v-if="!localEditMode"
                class="col-12"
            >
                <div class="h4 my-2">
                    {{ itemData.fname }} {{ itemData.lname }}
                </div>
            </div>

            <hot-edit-input
                v-if="localEditMode"
                label="First Name"
                v-model="itemData.fname"
                label-vertical
                :edit-mode="localEditMode"
                autofocus
                :loading="loading"
                required
                ref="fname"
                no-edit-class="font-weight-bold font-size-100"
                class="col-6"
            ></hot-edit-input>

            <hot-edit-input
                v-if="localEditMode"
                label="Last Name"
                v-model="itemData.lname"
                label-vertical
                :edit-mode="localEditMode"
                :loading="loading"
                :required="false"
                ref="lname"
                no-edit-class="font-weight-bold font-size-100"
                class="col-6"
            ></hot-edit-input>
            <!-- JULY 15 2022 - set required false on lname per Paul L (Convergix) request -->

        </b-form-row>
        <hr>
        <b-form-row class="">

            <hot-edit-input
                label="Email"
                v-model="itemData._email"
                type="email"
                :edit-mode="localEditMode"
                :no-edit="itemData.email_is_locked"
                :loading="loading"
                :required="emailRequired"
                ref="_email"
                label-vertical
                class="col-12"
            ></hot-edit-input>  <!-- label-cols="auto" -->
            <div v-if="localEditMode && itemData.email_is_locked" class="small text-muted col-12">
                ** This user's email address cannot be changed because they have already activated their account, or an invitation is outstanding.  Please contact Supplios support if you need to change this.
            </div>
            <div v-if="itemData.email_issues_since" class="mt-2 col-12 small text-danger">
                <b-icon icon="envelope-fill" variant="danger" class="mr-2"></b-icon>
                <span>
                    <b>Email Issues:</b>
                    There are delivery issues with this user's email address.  Please contact Supplios support for more information.
                </span>
            </div>

        </b-form-row>
        <hr>
        <b-form-row class="">

            <base-vendor-search-select
                v-if="!hideCompanyInput"
                :preselected-vendor="itemData.companyObj"
                label-vertical
                :no-edit="hasPresetCompany && !allowCompanyChange"
                empty-text="---"
                @input="handleVendorMultiselectInput"
                ref="newVendorSearchSelect"
                required
                :edit-mode="localEditMode"
                class="col-12"
            ></base-vendor-search-select>

            <hot-edit-input
                label="Job Title"
                v-model="itemData.job_title"
                empty-text="---"
                label-vertical
                :edit-mode="localEditMode"
                :loading="loading"
                ref="job_title"
                input-class=""
                class="col-6 mt-3"
            ></hot-edit-input>

            <hot-edit-input
                label="Work Location"
                :description="localEditMode ? 'Plant, office, or other location where person is based' : ''"
                v-model="itemData.work_location"
                empty-text="---"
                label-vertical
                :edit-mode="localEditMode"
                :loading="loading"
                ref="work_location"
                input-class=""
                class="col-6 mt-3"
            ></hot-edit-input>
            
        </b-form-row>
        <hr v-if="!createNew">
        <b-form-row v-if="!createNew" class="">

            <!-- <hot-edit-select
                label="User Role"
                v-model="itemData.role"
                label-vertical
                :edit-mode="localEditMode"
                :loading="loading"
                required
                :options="localEditMode ? enabledRoleOptions : allRoleOptions"
                value-field="id"
                text-field="name"
                ref="role"
                input-class="w-auto"
                class="col-6"
            ></hot-edit-select> -->

            <hot-edit-div
                v-if="!createNew"
                label="Portal Status"
                label-vertical
                :loading="loading"
                class="col-8"
            >
                <transition name="fade-fast" mode="out-in">

                    <div v-if="loadingPortalAccess" key="loadingPortalAccess" class="text-center">
                        <b-spinner small></b-spinner>
                    </div>

                    <div v-else key="notloadingPortalAccess">
                        <b-badge v-if="itemData.portal_access === 'full'" key="portal-full" variant="soft-success" class="text-uppercase">Active</b-badge>
                        <b-badge v-else-if="itemData.portal_access === 'open_invite'" key="portal-invited" variant="soft-primary" class="text-uppercase">Open Invite</b-badge>
                        <b-badge v-else-if="itemData.portal_access === 'expired_invite'" key="portal-invite-expired" variant="soft-warning" class="text-uppercase">Expired Invite</b-badge>
                        <b-badge v-else-if="itemData.portal_access === 'on_hold'" key="portal-on-hold" variant="soft-danger" class="text-uppercase">Deactivated</b-badge>
                        <b-badge v-else-if="itemData.portal_access === 'invite_ready'" key="portal-invite-ready" variant="soft-info" class="text-uppercase">Invite Ready</b-badge>
                        <b-badge v-else-if="itemData.portal_access === 'none'" key="portal-none" variant="soft-secondary" class="text-uppercase">No Access</b-badge>
                        <base-popover-icon 
                            v-if="itemData.manual_block" 
                            icon="exclamation-circle-fill" 
                            icon-variant="danger" 
                            title="Account On Hold"
                            class="ml-2"
                        >
                            This user's account has been placed on hold by Supplios.  Please contact Supplios support to remove this restriction.
                        </base-popover-icon>
                        <b-button
                            v-if="['open_invite', 'expired_invite', 'invite_ready'].includes(itemData.portal_access) && currentUserInfo.is_paid_seat"
                            key="reminderButton"
                            variant="minimal-primary"
                            size="sm"
                            class="ml-2"
                            @click="sendInviteReminder"
                        >   
                            <span v-if="itemData.portal_access === 'open_invite'" key="openInvite">Send Reminder</span>
                            <span v-if="itemData.portal_access === 'expired_invite'" key="expiredInvite">Renew Invite</span>
                            <span v-if="itemData.portal_access === 'invite_ready'" key="inviteReady">Send Invite</span>
                            <!-- should invite_ready be handled by sendInviteReminder? or inviteToPortal? -->
                        </b-button>
                        <b-button
                            v-if="itemData.portal_access === 'none' && itemData.can_be_invited && currentUserInfo.is_paid_seat"
                            key="inviteButton"
                            variant="minimal-primary"
                            size="sm"
                            class="ml-2"
                            @click="inviteToPortal"
                        >
                            <span>Invite to Portal</span>
                        </b-button>
                        <b-button
                            v-if="currentUserInfo.is_paid_seat && (itemData.portal_access === 'open_invite' || itemData.portal_access === 'full')"
                            key="removeAccessButton"
                            variant="minimal-danger"
                            size="sm"
                            class="ml-2"
                            @click="removePortalAccess"
                        >
                            {{ itemData.portal_access === 'open_invite' ? 'Revoke Invitation?' : 'Revoke Portal Access?' }}
                        </b-button>
                    </div>
                
                </transition>
                    
            </hot-edit-div>

            <hot-edit-div
                v-if="!createNew"
                label="Role"
                label-vertical
                :loading="loading"
                class="col-4"
            >
                <transition name="fade-fast" mode="out-in">

                    <div v-if="loadingPortalAccess" key="loadingPortalAccessRole" class="text-center">
                        <b-spinner small></b-spinner>
                    </div>

                    <!-- TODO - add buttons (like above) for upgrading/downgrading to admin/user -->

                    <div v-else key="notloadingPortalAccessRole">
                        <b-badge 
                            v-if="itemData.is_external_admin" 
                            key="isCompanyAdmin"
                            variant="soft-secondary" 
                            class="text-uppercase"
                            v-b-tooltip.hover 
                            title="This user is an Admin for their company"
                        >Admin</b-badge>
                        <span v-else key="notCompanyAdmin">User</span>
                    </div>
                
                </transition>

            </hot-edit-div>

        </b-form-row>

        <hr>
        <b-form-row class="">
            <div class="col-12">
                <!-- Oct 2026 - the TenantRoles this contact holds (was the is_def_* switches) - options come from the tenant_role_options endpoint -->
                <b-form-group
                    label="Default Contact?"
                    label-class="hot-edit-label mb-1"
                    :label-cols="null"
                    label-align="left"
                    :description="hasEmailAddress ? 'Types of tasks and workflows this person is a default contact for.' : 'No Email Address - must have email address to be picked as a default contact'"
                    class="hot-edit-input-group hot-edit-input mb-0"
                >
                    <div v-if="localEditMode" key="tenantRolesInput">
                        <b-form-checkbox
                            v-for="roleOpt in tenantRoleOptions"
                            :key="roleOpt.slug"
                            v-model="itemData.tenant_role_slugs"
                            :value="roleOpt.slug"
                            v-b-tooltip.hover.top="roleOpt.description"
                            inline
                            switch
                        >
                            {{ roleOpt.name }}
                        </b-form-checkbox>
                    </div>
                    <div v-else-if="!loading" key="tenantRolesDisplay" class="d-flex flex-wrap gap-4">
                        <div v-for="roleOpt in tenantRoleOptions" :key="roleOpt.slug" class="">
                            <b-icon 
                                :icon="itemData.tenant_role_slugs.includes(roleOpt.slug) ? 'check-circle-fill' : 'x-circle'" 
                                :variant="itemData.tenant_role_slugs.includes(roleOpt.slug) ? 'success' : 'danger'" 
                                class="mr-1"
                            ></b-icon>
                            <span>{{ roleOpt.name }}</span>
                        </div>
                    </div>
                </b-form-group>
            </div>

        </b-form-row>

        <hr>

        <TuPhoneNumbers
          ref="phoneNumbersForm"
          :company-id="companyId"
          :initialPhoneNumbers="itemData.phone_numbers || []" 
          :editMode="localEditMode"  
          @changed="handlePhoneNumbersChange($event)"
        />

        <div v-if="canShowNotes" class="row">
            <!-- currently only showing this for internal users -- backend has permissions control also, so just for convenience -->
            <div class="col-12">
                <hr>
            </div>
            <hot-edit-div
                label="Notes"
                label-vertical
                :loading="loading"
                class="col-12"
            >
                <TenantUserNotes :tuId="tuId" />
                <!-- we have this editable even when not in edit mode -- its basically its own thing 
                 will clean this up once you have the parent component refactored -->
            </hot-edit-div>
        </div>
        
        <hr v-if="!createNew">

        <b-form-row v-if="!createNew">
        
            <hot-edit-datepicker
                v-if="!createNew"
                label="Date Added"
                v-model="itemData.created"
                label-vertical
                :edit-mode="localEditMode"
                no-edit
                :loading="loading"
                ref="created"
                class="col-6"
            ></hot-edit-datepicker>

        </b-form-row>

        <div class="row">

            <div v-if="!allRefsValid" class="py-3 px-4">
                <h6 class="text-danger">Please fix these errors:</h6>
                <p 
                    v-for="message in refsInvalidList" 
                    :key="message" 
                    class="text-danger mb-1 small"
                >
                    {{ message }}
                </p>
            </div>

        </div>
        </b-overlay>
    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import { 
    HotEditInput, 
    HotEditDiv,
    //HotEditSelect, 
    //HotEditTextarea, 
    //HotEditSwitch,
    //HotEditMoney,
    //HotEditCheckboxGroup,
    HotEditDatepicker
} from '../js/custom-form-components';
import { currentUserInfoMixin } from '../js/custom-vue-mixins';
import BaseVendorSearchSelect from './BaseVendorSearchSelect';
import isEmpty from 'lodash/isEmpty';
import intersection from 'lodash/intersection';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import TuPhoneNumbers from './tenant_users/TuPhoneNumbers.vue';
import TenantUserNotes from './tenant_users/TenantUserNotes.vue';
import _ from 'lodash';

export default {
    name: 'ContactDetailForm',
    components: {
        HotEditInput,
        HotEditDiv,
        //HotEditSelect,
        //HotEditTextarea,
        //HotEditSwitch,
        //HotEditMoney,
        HotEditDatepicker,
        //HotEditCheckboxGroup,
        BaseVendorSearchSelect,
        TuPhoneNumbers,
        TenantUserNotes,
    },
    mixins: [currentUserInfoMixin],
    props: {
        tuId: {
            type: String,
            required: false,  // because we also use this to create new
        },
        editMode: {  
            // set to TRUE if/when you want to force to edit mode on mount, managed locally through localEditMode 
            // ideally you should call methods enterEditMode() and cancelEditMode() from parent
            type: Boolean,
            default: false,
        },
        createNew: {  // set this to true if using this to create new, not edit existing
            // if this is true, then pk prop and editMode will be IGNORED
            type: Boolean,
            default: false,
        },
        /*
        YOU HAVE 2 OPTIONS FOR PASSING IN / HARD-CODING COMPANY:
        1) put {company: companyId} in newData AND select hideCompanyInput=true -- this works if you never want to show the multiselect box... like when adding contacts on a company page
        2) pass company ID + name into companyData -- this allows you to still show/use the company multiselect, and because you're passing in full object with company name AND Id, it gives enough for showing something in the multiselect component
            - but by default, when you pass something into companyData, multiselect will be in read only mode (no edits)
            - to change that, make allowCompanyChange = true
        */
        newData: {  // if you want to FORCE any field values for CREATE NEW objects (not update existing), pass it in here
            // fields must be in same format/names as used in API
            type: Object,
            required: false,
        },
        hideCompanyInput: { // set to TRUE to NOT SHOW company input selector AT ALL (or pass in companyData if you just want to preset the company, still show it as read-only when adding)
            type: Boolean,
            default: false,
        },
        companyData: {  // pass FULL OBJECT for company you want to hard-code preset for adding a new contact
            // if this is set, it will set company: companyData.id in contact detail form, AND will render company input in read-only mode, displaying companyData.name (unless not present)
            // this will overrule newData.company prop if company id is set in both places
            type: Object,
            required: false,
        },
        allowCompanyChange: {
            // IF companyData is set / passed in, but you still want to allow the company to be changed from that preset value, set this to True
            type: Boolean,
            default: false,
        },
        noRefreshOnUpdate: {  // if you set this (to true) then it will not re-fetch updated object after update...
            // you SHOULD set this to true if you're embedding this in a modal or something else that will close the form after update
            type: Boolean,
            default: false,
        },
        // you could add a prop to show vendor search box or not... depending on where this is used
        fieldsToShow: {  // array of field names to SHOW, also limits which ones are used in update/save (does not include any/all nested objects like notes)
            type: Array,
            required: false,
        },
        emailRequired: {  // is email field required?  some places we need it, some we don't 
            type: Boolean,
            default: false,
        },
        hideNotes: {  // set to TRUE to HIDE notes section completely  (will be hidden if createNew is true also, or if viewer is not internal for now)
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error while getting vendor information.',
            error: false,
            loading: false,
            loadingPortalAccess: false,  // busy flag for when sending a reminder / renewal invite / removing access
            localEditMode: this.editMode,  // localEditMode controls things, but this allows parent to force edit mode on create/mount
            dataBeforeEdits: {},  // save here before doing edits
            // options for working with this object:
            // 1) Explicitly declare all the properties here so they are reactive
            // 2) use this.$set(this.itemData, 'newProp' when you fetch things from API
            // 3) use object generator or Object.assign({}, this.itemData, itemDataFromApi)... new itemData both here and when you load stuff from API
            itemData: {
                // fill in with all properties you want to use?  
                // or make a blank one you can Object.assign() when needed
            },
            blankItemData: {
                fname: '',
                lname: '',
                _email: '',
                job_title: '',
                work_location: '',
                created: null,
                portal_access: '',
                company: null,  // existing company ID goes here (only if existing, and only ID)
                new_company_name: '',  // NEW company name goes here (only if NEW)
                companyObj: {}, // set company object here, if any (with 'id' and 'name')
                tenant_role_slugs: [],  // write-only on the API: slugs of the TenantRoles this contact holds (seeded from tenant_roles on edit, is_default options on create)
            },
            tenantRoleOptions: [],  // [{id, slug, name, description, is_default}] from suppliosApi.contact.tenantRoleOptions()
            tenantRoleOptionsLoaded: false,  // only send tenant_role_slugs once we know the options (so a failed fetch never clears roles)
            allRefsValid: true,  // set true initially... then false if any invalid found when checking, this is used as flag to show refsInvalid
            refsInvalidList: [],  // set this in checkRefsValid()
        }
    },
    computed: {
        objectForUpdate: function() {
            // the vendor object we'll send to API for update -- based on fieldsToShow, if implemented
            let allFieldsOnObject = Object.keys(this.itemData);
            let fieldsToReturn = [...allFieldsOnObject];  // start with this, then take some out if needed
            if (this.fieldsToShow && this.fieldsToShow.length) {  // if we have something in fieldsToShow, then use it to limit things
                fieldsToReturn = intersection(this.fieldsToShow, allFieldsOnObject);
            }
            return pick(this.itemData, fieldsToReturn);
        },
        hasPresetCompany: function() {
            // do we have valid information passed in to preset the company?  
            return this.companyData && !isEmpty(this.companyData);
        },
        companyId: function() {
            // if we have companyData, return its ID, otherwise return undefined
            return this.itemData?.company ?? this.itemData?.companyObj?.id ?? this.companyData?.id;
        },
        tuFullName: function() {
            // get full_name for this TU from itemData.full_name, or stitch together as fallback
            if (this.itemData.full_name) return this.itemData.full_name;
            return `${this.itemData.fname} ${this.itemData.lname}`.trim();
        },
        hasEmailAddress() {
            // contacts without an email address are never picked as default contacts, even if they hold the roles
            return !!this.itemData?._email;
        },
        currentUserIsInternal() {
            // is current user an internal Supplios user?
            return this.currentUserInfo && this.currentUserInfo.is_internal;
        },
        canShowNotes() {
            return !this.createNew && !this.hideNotes && this.tuId && this.currentUserIsInternal;
        },
    },
    created: function() {
        // copy blankItemData to itemData for both create and update cases
        this.itemData = merge({}, this.blankItemData);
        this.getTenantRoleOptions();
        // handle create new
        if (this.createNew) {
            if (this.newData && !isEmpty(this.newData)) {
                this.itemData = merge({}, this.itemData, this.newData);
            }
            if (this.companyData && this.companyData.id) {
                // set hard-coded company ID after setting newData... companyData has priority over newData
                this.itemData.company = this.companyData.id;
                this.itemData.companyObj = Object.assign({}, this.companyData);
            }
            this.enterEditMode();
            //this.getAllCategories();  // haven't implemented yet... it is specific to each field
        } else {  // handle update
            this.getItemData().then(() => {
              console.log('created after getItemData');
              this.setDataBeforeEdits(this.itemData);
              this.$refs.phoneNumbersForm.setPhoneNumbers(this.itemData.phone_numbers)
            });
            //this.getAllCategories();  // haven't implemented yet... specific to each field
        }
    },
    methods: {
        /**
         * Save the data - with cloneDeep - to be able to compare the data before edits.
         * @param data 
         */
        setDataBeforeEdits(data) {
          this.dataBeforeEdits = _.cloneDeep(data);
        },
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            if (Array.isArray(message)) {
                this.errorMsg = message.join(', ');
            } else {
                this.errorMsg = message;
            }
            this.error = true;
        },
        objectToArray: function(content) {
            // used to take returned error objects and output them to array of strings
            let outputArray = [];
            if (Object.keys(content).length) {  // check if content has keys (is an object, really)
                for (const [key, value] of Object.entries(content)) {
                    outputArray.push(`${key}: ${value}`);
                }
            } else if (Array.isArray(content)) {
                outputArray = [...content];
            } else {
                outputArray = [content];
            }
            return outputArray;
        },
        handleVendorMultiselectInput: function(selectedVendorObj) {
            // this handles new selection of vendor/company from multiselect
            // it will pass up object of new or existing vendor -- if new, will have isNew: true key/value
            // then if EXISTING VENDOR, set itemData.company with the existing company ID
            // for NEW VENDOR, set itemData.new_company_name with the new company name only
            console.log('from handleVendorMultiselectInput, passed object is below');
            console.log(selectedVendorObj);
            if (selectedVendorObj && 'name' in selectedVendorObj) {  // first see if we have a real vendor object passed up
                if ('isNew' in selectedVendorObj && selectedVendorObj.isNew) {
                    // handle case where user entered a NEW VENDOR
                    this.itemData.new_company_name = selectedVendorObj.name;
                    this.itemData.companyObj.name = selectedVendorObj.name;
                    return;
                } else if ('id' in selectedVendorObj) {  // if isNew was not there, but still make sure we have an ID
                    // handle EXISTING VENDOR case where isNew was not in the passed up object
                    this.itemData.company = selectedVendorObj.id; 
                    this.itemData.companyObj.id = selectedVendorObj.id;
                    return;
                }
            } else {  // handle case the multiselect component was RESET / CLEARED and passed up null or empty object
                // need to clear everything here
                this.itemData.new_company_name = '';  // need to clear any previous name value
                this.itemData.company = null;
                this.itemData.companyObj = {};
                return;
            }
        },
        getTenantRoleOptions: async function() {
            // the TenantRoles a contact can hold -- on create, preselect the ones new contacts get by default (unless newData already set some)
            try {
                const { data } = await suppliosApi.contact.tenantRoleOptions();
                this.tenantRoleOptions = data;
                this.tenantRoleOptionsLoaded = true;
                if (this.createNew && !(this.newData && this.newData.tenant_role_slugs)) {
                    this.itemData.tenant_role_slugs = data.filter(opt => opt.is_default).map(opt => opt.slug);
                }
            } catch (error) {
                this.handleError(error, 'Error while getting contact role options');
            }
        },
        setLocalDataFromApi: function(respData) {
            // set data (from API response.data) to the structure we have here, mainly dealing with setting company data
            this.itemData = merge({}, this.itemData, respData);
            // tenant_roles (read-only objects) -> tenant_role_slugs (what we edit and send back) - assign, do not merge arrays
            this.itemData.tenant_role_slugs = (respData.tenant_roles || []).map(role => role.slug);
            // data from API currently has non-nested company data... respData.company (company ID) and respData.company_name (company name)
            // so check if they have those values, then build up itemData.companyObj object in local data, this gets used by BaseVendorSearchSelect component
            // do them separately becuase BaseVendorSearchSelect can handle only getting ID without the name (should...)
            if (respData.company) {
                this.itemData.companyObj.id = respData.company;
            }
            if (respData.company_name) {
                this.itemData.companyObj.name = respData.company_name;
            }
            // no return... it just sets this.itemData 
        },
        getItemData: function() {
            this.loading = true;
            return suppliosApi.contact.retrieve(this.tuId)
            .then(response => {
                const respData = response.data;
                this.setLocalDataFromApi(respData);
                //this.itemData = merge({}, this.itemData, respData);
            })
            .catch(error => this.handleError(error, 'Error while getting data'))
            .finally(() => {
                this.loading = false;
                //this.localEditMode = false;
            })
        },
        formatDataForSave: function() {
            // grabs current data from this.itemData (cloneDeep), picks off only the fields you want to send to API in the correct format / field names
            //let fields_to_save = ['fname', 'lname', '_email', 'job_title', 'company'];
            let snapshotData = cloneDeep(this.itemData);
            delete snapshotData.tenant_roles;  // read-only on the API -- we send tenant_role_slugs instead
            if (!this.tenantRoleOptionsLoaded) {
                delete snapshotData.tenant_role_slugs;  // options never loaded -- leave the contact's roles as they are
            }
            //let returnObj = pick(snapshotData, fields_to_save);
            //return returnObj;
            return snapshotData;
        },
        resetRefsValid: function() {
            // resets all refs you checked to default validation state so things aren't always in red outline if validation problems
            let allRefs = Object.values(this.$refs);
            allRefs.forEach(ref => {
                if (ref && Object.keys(ref).includes('resetValidity')) {  // have to check if refs are actually present and if they have resetValidity method
                    ref.resetValidity();  // do the reset
                }
            });
        },
        checkRefsValid: function() {
            let allRefs = Object.values(this.$refs);  // this.$refs is an object, Object.values() makes an array of actual refs
            let refsInvalidList = [];  // we'll make this below... a list of errors from checking refs
            let allRefsValid = true;  // set it to true here, then set to false if you find any invalid in loop below
            allRefs.forEach(ref => {
                if (ref) {  // have to check if refs are actually present
                    let refKeys = Object.keys(ref);
                    if (refKeys.includes('checkValidity')) {
                        let isRefValid = ref.checkValidity();
                        if (isRefValid === false) {  // need to specifically check if 'false' -- because some hot-edit-inputs return 'null' if they aren't in edit mode, we'll ignore those
                            refsInvalidList.push(ref.checkInvalidMessage());
                            allRefsValid = false;
                        }
                    }
                }
            });
            this.refsInvalidList = refsInvalidList;
            this.allRefsValid = allRefsValid;
            return allRefsValid;  // returns a Boolean
        },
        /**
         * To check if all phone numbers are valid.
         */
        isAllPhoneNumbersValid: function() {
            return this.$refs.phoneNumbersForm.isAllValid();
        },
        updateItemData: async function() { // CAN BE CALLED FROM PARENT - used for updating existing
          try {
              if (!this.checkRefsValid() || !this.isAllPhoneNumbersValid()) {
                return false 
              }
              this.loading = true;
              let updateObject = this.formatDataForSave();
              console.log('updateItemData - here is what we will send to api');
              console.log(updateObject);
              const response = await suppliosApi.contact.partialUpdate(this.tuId, updateObject);
              const savePhoneNumbersResponse = await this.savePhoneNumbers(this.tuId, this.itemData.phone_numbers)
              this.$emit('update-complete', response.data);
              console.log('contact response', response, 'save phone numbers response', savePhoneNumbersResponse);
              this.$toast.success('Contact successfully updated');
              if (!this.noRefreshOnUpdate) {  // only refresh data if we are not in a modal (this is a prop)
                  this.getItemData();
              }
              this.exitEditMode();
            }
            catch(error) {
              this.handleError(error, this.objectToArray(error.response?.data || {}));  // error will likely be an object with key = field name and value = error message
            }
            finally {
              this.loading = false;
            }
        },
        /**
         * Saves the phone numbers of a tu using its dedicated endpoints
         * @param {string} tuId 
         * @param {array} phoneNumbers 
         */
        savePhoneNumbers(tuId, phoneNumbers) {
          return suppliosApi.phoneNumbers.updatePhoneNumbersForTu(tuId, phoneNumbers)    
        },
        saveNewItem: async function() {  // CAN BE CALLED FROM PARENT - for adding new thing, not updating existing
          try {            
            if (!this.checkRefsValid() || !this.isAllPhoneNumbersValid()) {
              return false;
            }
            this.loading = true;
            let saveObject = this.formatDataForSave();
            console.log('savePhoneNumbers - here is what we will send to api');
            console.log(saveObject);
            const saveResponse = await suppliosApi.contact.create(saveObject)
            const newTuId = saveResponse?.data?.id;
            if(!newTuId) {
              throw new Error('Error while creating new contact!');
            }
            const phoneNumberResponse = await this.savePhoneNumbers(newTuId, this.itemData.phone_numbers);
            this.$emit('save-complete', saveResponse.data);
            console.log('saveResponse', saveResponse, 'phoneNumberResponse', phoneNumberResponse);
            this.$toast.success("Contact successfully saved");
          }
          catch(error) {  // if you have error, keep the form open and in edit mode, showing errors, allowing user to correct things
            this.handleError(error, this.objectToArray(error.response.data));  // error will likely be an object with key = field name and value = error message
          }
          finally {
              this.loading = false;
          }
        },
        sendInviteReminder: function() {
            this.loadingPortalAccess = true;
            return suppliosApi.contact.sendInviteReminder(this.tuId)
                .then(response => {
                    // it returns an updated version of the TU instance -- set it on local data, no need to re-fetch item data
                    // this is different than update/create modals, a bit, since in those you will close the modal via emitting an event
                    // with this one, you'll still emit an event, but likely the modal will stay open
                    this.setLocalDataFromApi(response.data);
                    this.$toast.success("Reminder sent");
                    this.$emit('access-updated', response.data);
                    // this.getItemData();
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while sending reminder email');
                    return false;
                })
                .then(status => {
                    this.loadingPortalAccess = false;
                    return status;
                });
        },
        inviteToPortal: function() {
            this.loadingPortalAccess = true;
            let inviteData = {
                detail_response: true,  // our endpoint checks for this... if false, it won't send a detailed response of the updated TU/contact
            };
            return suppliosApi.contact.inviteToPortal(this.tuId, inviteData)
                .then(response => {
                    // response.data will be updated detail of this TU contact if you pass detail_response: true, otherwise just a simple 'invite': 'sent'
                    // since this is a detail view, get the detail_response
                    this.setLocalDataFromApi(response.data);
                    //console.log(response.data);
                    this.$toast.success("Invitation sent");
                    this.$emit('access-updated', response.data);  // now emit event to let parent know it was updated
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while performing the invite');
                    return false;
                })
                .then(status => {
                    this.loadingPortalAccess = false;
                    return status;
                });
        },
        removePortalAccess: function() {
            this.$bvModal.msgBoxConfirm(`Are you sure you want to remove access for ${this.tuFullName}?  They will no longer be able to login.`, {
                title: 'Please Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Remove Access',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
                })
                .then(value => {
                    if (value) this.performRemovePortalAccess();  // only proceed if value is true (clicked on OK)
                })
                .catch(err => {
                    this.handleError(err, 'Error while removing access for this user.');
                });
        },
        performRemovePortalAccess: function() {
            this.loadingPortalAccess = true;
            let postData = {
                detail_response: true,  // endpoint supports this, if true, we'll get full serialized TU object back (so we can render update right away)
            };
            return suppliosApi.contact.removePortalAccess(this.tuId, postData)
                .then(response => {
                    // response.data will be updated detail of this TU contact if you pass detail_response: true, otherwise just a simple 'result': 'removed access' or something like that
                    // since this is a detail view, get the detail_response so we can update data right away
                    this.setLocalDataFromApi(response.data);
                    //console.log(response.data);
                    this.$toast.success("Access removed");
                    this.$emit('access-updated', response.data);  // now emit event to let parent know it was updated
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while removing access for this user');
                    return false;
                })
                .then(status => {
                    this.loadingPortalAccess = false;
                    return status;
                });
        },
        clearErrors: function() {
            // call this at least when exiting editMode because you're resetting values anyways
            this.error = false;
            this.resetRefsValid(); // set all refs to their default validation visible state
            this.allRefsValid = true;
            this.refsInvalidList = [];
        },
        // functions below can be called from parent (or locally) to change mode of this form
        enterEditMode: function() {
            // this.dataBeforeEdits = {...this.itemData};  // store this so you have it if you edit then cancel
            this.setDataBeforeEdits(this.itemData);
            this.localEditMode = true;
            this.$emit('edit-on');
        },
        cancelEditMode: function() {
            this.itemData = {...this.dataBeforeEdits};
            this.$refs.phoneNumbersForm.setPhoneNumbers(this.dataBeforeEdits?.phone_numbers || [])
            this.localEditMode = false;
            this.clearErrors();
            this.$emit('edit-off');
        },
        exitEditMode: function() { // exit edit mode, but don't reset item data
            this.localEditMode = false;
            this.$emit('edit-off');
        },
        /**
         * Handles and updates the phone numbers in itemsData
         * @param {Array} event 
         */
        handlePhoneNumbersChange(event) {
          console.log('handlePhoneNumbersChange', event);
          this.$set(this.itemData, 'phone_numbers', event);
        }
    },
}
</script>

<style>

.input-height-40 {
    height: 40px;
}

</style>
